import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faSync,
	faChevronLeft,
	faChevronRight,
	faCarSide,
	faFlag,
	faCheck,
	faTimes,
} from "@fortawesome/free-solid-svg-icons"
import {
	fetchLocationFleet,
	fetchLocationCalendar,
} from "../../store/actions/locationGererActions"
import { checkLocationsLate } from "../../store/actions/locationGererActions"
import { COLORS } from "../../utilities/Constants"
import { generalStyles } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"

// ---- Helpers date (local, lundi 1er jour) ---------------------------------
const pad = (n) => String(n).padStart(2, "0")
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const addDays = (d, n) => {
	const x = new Date(d)
	x.setDate(x.getDate() + n)
	return x
}
const startOfWeek = (d) => {
	const x = new Date(d)
	const day = (x.getDay() + 6) % 7 // lundi = 0
	x.setHours(0, 0, 0, 0)
	x.setDate(x.getDate() - day)
	return x
}
const MOIS = [
	"Janvier",
	"Février",
	"Mars",
	"Avril",
	"Mai",
	"Juin",
	"Juillet",
	"Août",
	"Septembre",
	"Octobre",
	"Novembre",
	"Décembre",
]
const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]

// Couleur stable par véhicule (pastilles de location).
const PALETTE = [
	"#2563EB",
	"#16A34A",
	"#D97706",
	"#DC2626",
	"#7C3AED",
	"#0891B2",
	"#DB2777",
	"#65A30D",
]
const colorFor = (id) => PALETTE[Math.abs(id || 0) % PALETTE.length]

const navBtn = {
	width: 32,
	height: 32,
	borderRadius: 8,
	border: `1px solid ${COLORS.border}`,
	backgroundColor: COLORS.surface,
	color: COLORS.text,
	cursor: "pointer",
	fontSize: 12,
}

const LocationGererV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { fleet, events, fetchEvents } = useSelector(
		(s) => s.locationGererInfo,
	)
	const lateLocs = useSelector((s) => s.locationGererInfo.lateLocs)

	const [view, setView] = useState("month") // "month" | "week"
	const [anchor, setAnchor] = useState(() => {
		const d = new Date()
		d.setHours(0, 0, 0, 0)
		return d
	})
	const [vehiculeFilter, setVehiculeFilter] = useState(0)

	// Jours affichés + bornes de la fenêtre (mois = grille 6×7 ; semaine = 7).
	const { days, start, end } = useMemo(() => {
		if (view === "week") {
			const s = startOfWeek(anchor)
			const ds = Array.from({ length: 7 }, (_, i) => addDays(s, i))
			return { days: ds, start: toISO(s), end: toISO(addDays(s, 6)) }
		}
		const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
		const s = startOfWeek(first)
		const ds = Array.from({ length: 42 }, (_, i) => addDays(s, i))
		return { days: ds, start: toISO(s), end: toISO(addDays(s, 41)) }
	}, [view, anchor])

	useEffect(() => {
		dispatch(fetchLocationFleet())
		dispatch(checkLocationsLate())
	}, [dispatch])

	useEffect(() => {
		dispatch(fetchLocationCalendar({ start, end, vehicule: vehiculeFilter }))
	}, [dispatch, start, end, vehiculeFilter])

	// Map jourISO → events couvrant ce jour.
	const eventsByDay = useMemo(() => {
		const map = {}
		days.forEach((d) => (map[toISO(d)] = []))
		const lastISO = toISO(days[days.length - 1])
		events.forEach((ev) => {
			const finISO = ev.fin || lastISO
			days.forEach((d) => {
				const iso = toISO(d)
				if (ev.debut <= iso && iso <= finISO) map[iso].push(ev)
			})
		})
		return map
	}, [events, days])

	const todayISO = toISO(new Date())
	const currentMonth = anchor.getMonth()

	const periodLabel =
		view === "week"
			? `Semaine du ${days[0].getDate()} ${MOIS[days[0].getMonth()]} ${days[0].getFullYear()}`
			: `${MOIS[currentMonth]} ${anchor.getFullYear()}`

	const shift = (dir) =>
		setAnchor((a) =>
			view === "week"
				? addDays(a, dir * 7)
				: new Date(a.getFullYear(), a.getMonth() + dir, 1),
		)
	const goToday = () => {
		const d = new Date()
		d.setHours(0, 0, 0, 0)
		setAnchor(d)
	}
	const openLoc = (id) => history.push(`/location/fiche/${id}`)

	const dayCellHeight = view === "week" ? 220 : 96

	return (
		<div style={generalStyles.page}>
			<div style={{ maxWidth: 1120, margin: "0 auto" }}>
				<h2 style={{ ...generalStyles.title, marginBottom: 24 }}>
					Gérer les locations
				</h2>

				{/* ---- Calendrier ---- */}
				<div style={{ ...generalStyles.card, marginBottom: 28 }}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							flexWrap: "wrap",
							gap: 12,
							marginBottom: 16,
						}}
					>
						<div
							style={{
								...generalStyles.segmented,
								marginBottom: 0,
							}}
						>
							{[
								["month", "Mois"],
								["week", "Semaine"],
							].map(([key, label]) => (
								<button
									key={key}
									type="button"
									onClick={() => setView(key)}
									style={generalStyles.segment(view === key)}
								>
									{label}
								</button>
							))}
						</div>

						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 10,
							}}
						>
							<button
								type="button"
								style={navBtn}
								onClick={() => shift(-1)}
								title="Précédent"
							>
								<FontAwesomeIcon icon={faChevronLeft} />
							</button>
							<span
								style={{
									minWidth: 180,
									textAlign: "center",
									fontSize: 14,
									fontWeight: 600,
									color: COLORS.text,
								}}
							>
								{periodLabel}
							</span>
							<button
								type="button"
								style={navBtn}
								onClick={() => shift(1)}
								title="Suivant"
							>
								<FontAwesomeIcon icon={faChevronRight} />
							</button>
							<button
								type="button"
								style={{
									...generalStyles.btnSecondary,
									padding: "6px 14px",
								}}
								onClick={goToday}
							>
								Aujourd'hui
							</button>
						</div>

						<select
							style={{ ...generalStyles.select, maxWidth: 220 }}
							value={vehiculeFilter}
							onChange={(e) =>
								setVehiculeFilter(parseInt(e.target.value))
							}
						>
							<option value={0}>Tous les véhicules</option>
							{fleet.map((v) => (
								<option key={v.id} value={v.id}>
									{`${v.marque} ${v.modele}`.trim()}
									{v.plaques ? ` (${v.plaques})` : ""}
								</option>
							))}
						</select>
					</div>

					{/* En-tête jours */}
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(7, 1fr)",
							gap: 6,
							marginBottom: 6,
						}}
					>
						{JOURS.map((j) => (
							<div
								key={j}
								style={{
									fontSize: 11,
									fontWeight: 600,
									textTransform: "uppercase",
									letterSpacing: "0.05em",
									color: COLORS.textLight,
									textAlign: "center",
								}}
							>
								{j}
							</div>
						))}
					</div>

					{/* Grille */}
					<div style={{ position: "relative" }}>
						{fetchEvents.loading && (
							<div
								style={{
									position: "absolute",
									top: 8,
									right: 8,
									color: COLORS.textLight,
								}}
							>
								<FontAwesomeIcon icon={faSync} spin />
							</div>
						)}
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(7, 1fr)",
								gap: 6,
							}}
						>
							{days.map((d) => {
								const iso = toISO(d)
								const inMonth =
									view === "week" || d.getMonth() === currentMonth
								const isToday = iso === todayISO
								return (
									<div
										key={iso}
										style={{
											minHeight: dayCellHeight,
											border: `1px solid ${COLORS.border}`,
											borderRadius: 8,
											padding: 5,
											backgroundColor: isToday
												? COLORS.accentLight
												: inMonth
													? COLORS.surface
													: COLORS.bg,
											opacity: inMonth ? 1 : 0.55,
											display: "flex",
											flexDirection: "column",
											gap: 3,
											overflow: "hidden",
										}}
									>
										<div
											style={{
												fontSize: 12,
												fontWeight: isToday ? 700 : 500,
												color: isToday
													? COLORS.accent
													: COLORS.textMuted,
												textAlign: "right",
											}}
										>
											{d.getDate()}
										</div>
										{eventsByDay[iso].map((ev) => {
											const c = colorFor(ev.vehicule_id)
											const isStart = ev.debut === iso
											const isEnd = ev.fin === iso
											return (
												<div
													key={`${ev.id}_${iso}`}
													onDoubleClick={() =>
														openLoc(ev.id)
													}
													title={`${ev.vehicule_label} — ${ev.client} (${ev.etatTexte})`}
													style={{
														display: "flex",
														alignItems: "center",
														gap: 5,
														fontSize: 11,
														fontWeight: 500,
														color: "#fff",
														backgroundColor: c,
														borderRadius: 5,
														padding: "2px 6px",
														cursor: "pointer",
														whiteSpace: "nowrap",
														overflow: "hidden",
														textOverflow: "ellipsis",
														opacity: ev.restitue
															? 0.55
															: 1,
													}}
												>
													{isStart && (
														<FontAwesomeIcon
															icon={faCarSide}
															style={{ fontSize: 9 }}
														/>
													)}
													{isEnd && (
														<FontAwesomeIcon
															icon={faFlag}
															style={{ fontSize: 9 }}
														/>
													)}
													<span
														style={{
															overflow: "hidden",
															textOverflow:
																"ellipsis",
														}}
													>
														{ev.vehicule_label} ·{" "}
														{ev.client}
													</span>
												</div>
											)
										})}
									</div>
								)
							})}
						</div>
					</div>
				</div>

				{/* ---- Locations en retard ---- */}
				{lateLocs.length > 0 && (
					<div style={{ marginBottom: 28 }}>
						<h3
							style={{
								fontSize: 16,
								fontWeight: 600,
								color: COLORS.danger,
								margin: "0 0 12px",
							}}
						>
							Locations en retard ({lateLocs.length})
						</h3>
						<div style={tableStyles.container}>
							<table style={tableStyles.table}>
								<thead>
									<tr>
										<th style={{ ...tableStyles.th, width: 40 }}>
											N°
										</th>
										<th style={tableStyles.th}>Client</th>
										<th style={tableStyles.th}>Véhicule</th>
										<th style={tableStyles.th}>Début</th>
										<th style={tableStyles.th}>Retour prévu</th>
										<th style={tableStyles.th}>Retard</th>
									</tr>
								</thead>
								<tbody>
									{lateLocs.map((l, i) => (
										<tr
											key={l.id}
											onDoubleClick={() => openLoc(l.id)}
											style={{
												cursor: "pointer",
												backgroundColor: tableStyles.rowBg(i),
											}}
										>
											<td style={tableStyles.td}>{i + 1}</td>
											<td
												style={{
													...tableStyles.td,
													textAlign: "left",
												}}
											>
												{l.client}
											</td>
											<td
												style={{
													...tableStyles.td,
													textAlign: "left",
												}}
											>
												{l.veh}
											</td>
											<td
												style={{
													...tableStyles.td,
													textAlign: "left",
												}}
											>
												{l.debut}
											</td>
											<td
												style={{
													...tableStyles.td,
													textAlign: "left",
												}}
											>
												{l.resti}
											</td>
											<td
												style={{
													...tableStyles.td,
													textAlign: "left",
													color: COLORS.danger,
													fontWeight: 500,
												}}
											>
												{l.retard}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				)}

				{/* ---- Mes véhicules de location ---- */}
				<h3
					style={{
						fontSize: 16,
						fontWeight: 600,
						color: COLORS.text,
						margin: "0 0 12px",
					}}
				>
					Mes véhicules de location
				</h3>
				<div style={tableStyles.container}>
					<table style={tableStyles.table}>
						<thead>
							<tr>
								<th style={tableStyles.th}>Plaques</th>
								<th style={tableStyles.th}>Marque</th>
								<th style={tableStyles.th}>Modèle</th>
								<th style={tableStyles.th}>Status</th>
								<th style={{ ...tableStyles.th, textAlign: "center" }}>
									Libre
								</th>
							</tr>
						</thead>
						<tbody>
							{fleet.length === 0 ? (
								<tr>
									<td colSpan={5} style={tableStyles.empty}>
										Aucun véhicule de location.
									</td>
								</tr>
							) : (
								fleet.map((v, i) => (
									<tr
										key={v.id}
										onDoubleClick={() =>
											v.locId ? openLoc(v.locId) : null
										}
										style={{
											cursor: v.locId ? "pointer" : "default",
											backgroundColor: tableStyles.rowBg(i),
										}}
									>
										<td
											style={{
												...tableStyles.td,
												textAlign: "left",
												fontWeight: 500,
											}}
										>
											{v.plaques}
										</td>
										<td
											style={{
												...tableStyles.td,
												textAlign: "left",
											}}
										>
											{v.marque}
										</td>
										<td
											style={{
												...tableStyles.td,
												textAlign: "left",
											}}
										>
											{v.modele}
										</td>
										<td
											style={{
												...tableStyles.td,
												textAlign: "left",
											}}
										>
											{v.statusLabel}
										</td>
										<td
											style={{
												...tableStyles.td,
												textAlign: "center",
											}}
										>
											{v.libre ? (
												<FontAwesomeIcon
													icon={faCheck}
													style={{ color: COLORS.success }}
													title="Libre"
												/>
											) : (
												<FontAwesomeIcon
													icon={faTimes}
													style={{ color: COLORS.danger }}
													title="En location"
												/>
											)}
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

export default LocationGererV2
